import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';

import { getCsrfToken } from 'api/interceptors/userInterceptor';
import { recordRetryAfter } from 'lib/api/rateLimitRetry';
import { API_BASEURL } from 'lib/urls';
import store from 'store';
import 'store/configureRTK';

// `fetchBaseQuery` builds its error object from status + body only and
// discards the Response's headers, so a Rack::Attack 429 with a
// `Retry-After: N` header loses N by the time an endpoint's `catch`
// sees the error. Intercept in `fetchFn` where the raw Response is
// still in hand and stash the parsed delay in a module-level slot
// that `withRateLimitRetry` reads-and-clears on its next backoff.
const rateLimitCapturingFetch: typeof fetch = async (input, init) => {
  const response = await fetch(input, init);
  if (response.status === 429) {
    recordRetryAfter(response.headers.get('Retry-After'));
  }
  return response;
};

export const api = createApi({
  reducerPath: 'api',
  baseQuery: retry(
    fetchBaseQuery({
      baseUrl: API_BASEURL,
      fetchFn: rateLimitCapturingFetch,
      prepareHeaders: (headers) => {
        headers.set('X-CSRF-Token', getCsrfToken());
        return headers;
      },
    }),
    {
      retryCondition: () => false, // disable retries by default
    },
  ),
  tagTypes: [
    'Files',
    'Token',
    'Notes',
    'Websites',
    'Battlecards',
    'Products',
    'Project',
    'Projects',
    'ComposerCards',
    'Sources',
    'UseCases',
    'Overviews',
    'KnowledgeHubStepsStatus',
    'KnowledgeHubProducts',
    'KnowledgeHubPositioning',
    'KnowledgeHubExtractProducts',
    'KnowledgeHubExtractPositioning',
    'KnowledgeHubSegments',
    'KnowledgeHubExtractVariables',
    'Tags',
    'Users',
    'CurrentUser',
    'Rivals',
    'UserCounts',
    'IdentityProvider',
    'Company',
    'DealTips',
    'CardSchedule',
    'BoardSettings',
  ],
  endpoints: () => ({}), // endpoints are injected for code-splitting
});

const apiModel = {
  name: 'api',
  state: {},
  reducers: {},
  baseReducer: api.reducer,
};

store.addMiddleware(api.middleware);
store.addModel(apiModel);
